import { useCallback, useEffect, useRef, useState } from "react";
import { readPendingSyncItems, type PendingSyncItem } from "../lib/offline";
import { PENDING_CHANGED } from "../lib/pending-entries";
import { syncPendingEntries } from "../lib/sync-pending-entries";

export function usePendingSync(onSaved: () => void, enabled: boolean) {
  const active = useRef(false);
  const savedCallback = useRef(onSaved);
  savedCallback.current = onSaved;
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [items, setItems] = useState<PendingSyncItem[]>(() =>
    readPendingSyncItems(),
  );
  const syncNow = useCallback(async () => {
    if (!enabled || active.current || !navigator.onLine || !readPendingSyncItems().length) return;
    active.current = true;
    setSyncing(true);
    setSyncError("");
    try {
      const result = await syncPendingEntries();
      setSyncError(result.errors.join(" "));
      if (result.confirmed) savedCallback.current();
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : "Could not sync. Please try again.");
    } finally {
      setItems(readPendingSyncItems());
      active.current = false;
      setSyncing(false);
    }
  }, [enabled]);
  useEffect(() => {
    const update = () => setItems(readPendingSyncItems());
    const retry = () => { update(); void syncNow(); };
    retry();
    const interval = window.setInterval(retry, 30000);
    window.addEventListener("focus", retry);
    window.addEventListener("online", retry);
    window.addEventListener("offline", update);
    window.addEventListener("storage", retry);
    window.addEventListener(PENDING_CHANGED, update);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", retry);
      window.removeEventListener("online", retry);
      window.removeEventListener("offline", update);
      window.removeEventListener("storage", retry);
      window.removeEventListener(PENDING_CHANGED, update);
    };
  }, [syncNow]);
  return { items, syncing, syncError, syncNow };
}
